import { supabase } from '@/integrations/supabase/client';
import { cropToExact } from '@/lib/imageUtils';
import type { HomeBanner } from '@/types/homeBanners';

// ---- Upload helper ----
async function uploadHomeBannerImage(
  blob: Blob,
  variant: 'desktop' | 'mobile'
): Promise<string> {
  const fileName = `${variant}/${Date.now()}.webp`;
  const { error } = await supabase.storage
    .from('home-banners')
    .upload(fileName, blob, { contentType: 'image/webp', upsert: true });
  if (error) throw error;
  const { data } = supabase.storage.from('home-banners').getPublicUrl(fileName);
  return data.publicUrl;
}

// ---- Public ----
export async function fetchHomeBanners(): Promise<HomeBanner[]> {
  const { data, error } = await supabase
    .from('home_banners' as any)
    .select('*')
    .eq('is_active', true)
    .order('position');
  if (error) throw error;
  return (data || []) as unknown as HomeBanner[];
}

// ---- Admin ----
export async function fetchAllHomeBanners(): Promise<HomeBanner[]> {
  const { data, error } = await supabase
    .from('home_banners' as any)
    .select('*')
    .order('position');
  if (error) throw error;
  return (data || []) as unknown as HomeBanner[];
}

export async function createHomeBanner(
  desktopBlob: Blob,
  mobileBlob: Blob,
  linkUrl?: string,
  buttonLabel?: string,
  buttonColor?: string
): Promise<void> {
  const [desktopUrl, mobileUrl] = await Promise.all([
    uploadHomeBannerImage(desktopBlob, 'desktop'),
    uploadHomeBannerImage(mobileBlob, 'mobile'),
  ]);

  const { data: maxPos } = await supabase
    .from('home_banners' as any)
    .select('position')
    .order('position', { ascending: false })
    .limit(1)
    .maybeSingle();

  const { error } = await supabase
    .from('home_banners' as any)
    .insert({
      desktop_image_url: desktopUrl,
      mobile_image_url: mobileUrl,
      link_url: linkUrl || null,
      button_label: buttonLabel || null,
      button_color: buttonColor || 'orange',
      position: ((maxPos as any)?.position ?? -1) + 1,
    } as any);
  if (error) throw error;
}

export async function updateHomeBanner(
  id: string,
  updates: Partial<Pick<HomeBanner, 'link_url' | 'button_label' | 'button_color' | 'is_active' | 'position' | 'auto_rotate_seconds'>>
): Promise<void> {
  const { error } = await supabase
    .from('home_banners' as any)
    .update(updates as any)
    .eq('id', id);
  if (error) throw error;
}

export async function updateHomeBannerImage(
  id: string,
  blob: Blob,
  variant: 'desktop' | 'mobile'
): Promise<void> {
  const url = await uploadHomeBannerImage(blob, variant);
  const field = variant === 'desktop' ? 'desktop_image_url' : 'mobile_image_url';
  const { error } = await supabase
    .from('home_banners' as any)
    .update({ [field]: url } as any)
    .eq('id', id);
  if (error) throw error;
}

export async function deleteHomeBanner(id: string): Promise<void> {
  const { error } = await supabase
    .from('home_banners' as any)
    .delete()
    .eq('id', id);
  if (error) throw error;
}

// ---- Click tracking ----
export async function trackBannerClick(bannerId: string): Promise<void> {
  await supabase
    .from('home_banner_clicks' as any)
    .insert({ banner_id: bannerId } as any);
}

export async function fetchBannerClickCounts(): Promise<Record<string, number>> {
  const { data, error } = await supabase.rpc('get_banner_click_counts' as any);
  if (error) throw error;
  const map: Record<string, number> = {};
  for (const row of (data || []) as any[]) {
    map[row.banner_id] = Number(row.click_count);
  }
  return map;
}
